import { Link } from "react-router-dom";
import { SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Preguntas frecuentes",
    label: "Preguntas",
    title: "Lo que suelen preguntar antes de empezar.",
    cta: "Todas las preguntas",
    faqs: [
      {
        q: "¿Qué incluye la reunión inicial gratuita de 30 minutos?",
        a: "Es una conversación para conocer tu empresa y sus necesidades, hablar de la situación actual a nivel general, escuchar las inquietudes o prioridades que plantees y definir posibles siguientes pasos. No incluye auditoría SEO, diagnóstico técnico, reporte, investigación de palabras clave, análisis competitivo ni hoja de ruta, y no garantiza hallazgos.",
      },
      {
        q: "¿En qué se diferencia de una auditoría?",
        a: "La reunión inicial es una conversación para orientar los siguientes pasos. Una auditoría es un trabajo contratado: revisión sistemática de rastreo, indexación, contenido, enlaces y medición, con hallazgos documentados y priorizados. La reunión indica si conviene revisar algo; la auditoría explica qué, por qué y en qué orden.",
      },
      {
        q: "¿Conviene SEO, Google Ads o rediseño primero?",
        a: "Depende de dónde esté el cuello de botella. Si el sitio impide que te encuentren o te comprendan, primero lo técnico y el contenido. Si el sitio funciona pero no hay demanda capturada a corto plazo, Google Ads permite validar intención más rápido. El rediseño se justifica cuando la estructura o la propuesta impiden avanzar; en ese caso se planifica para no perder la visibilidad existente.",
      },
      {
        q: "¿Trabajas con empresas fuera de Houston?",
        a: "Sí. La sede está en el área de Houston, Texas, y se atienden proyectos de forma remota en Estados Unidos, en inglés y español. Atender un mercado no significa tener una oficina en él.",
      },
      {
        q: "¿Cómo se define el alcance y la inversión?",
        a: "A partir del contexto del negocio, los objetivos y lo que la investigación indique como prioritario. El alcance se acuerda por escrito antes de empezar e indica qué se ejecuta, qué se especifica para tu equipo y qué queda fuera. En proyectos con publicidad, los honorarios de consultoría son independientes de la inversión en medios.",
      },
      {
        q: "¿Cómo se mide el avance?",
        a: "Con las fuentes disponibles en tu propio entorno: Search Console, analítica, plataformas publicitarias y el registro de contactos. Se revisan visibilidad, oportunidades generadas y calidad del contacto. No se prometen posiciones, volumen de tráfico ni un retorno concreto.",
      },
    ],
  },
  en: {
    aria: "Frequently asked questions",
    label: "Questions",
    title: "What people usually ask before starting.",
    cta: "All questions",
    faqs: [
      {
        q: "What does the free 30-minute initial consultation include?",
        a: "It is a conversation to understand your company and its needs, discuss the current situation at a general level, hear the concerns or priorities you raise and determine possible next steps. It does not include an SEO audit, technical diagnosis, report, keyword research, competitor analysis or roadmap, and no findings are guaranteed.",
      },
      {
        q: "How is it different from an audit?",
        a: "The initial consultation is a conversation to guide next steps. An audit is contracted work: a systematic review of crawling, indexing, content, links and measurement, with documented and prioritized findings. The consultation indicates whether something is worth reviewing; the audit explains what, why and in which order.",
      },
      {
        q: "Should I start with SEO, Google Ads or a redesign?",
        a: "It depends on where the bottleneck is. If the site prevents people from finding or understanding you, technical work and content come first. If the site works but there is no demand captured in the short term, Google Ads validates intent faster. A redesign is justified when structure or positioning block progress; in that case it is planned so existing visibility is not lost.",
      },
      {
        q: "Do you work with companies outside Houston?",
        a: "Yes. The base is in the Houston, Texas area, and projects are handled remotely across the United States, in English and Spanish. Serving a market does not mean having an office in it.",
      },
      {
        q: "How are scope and investment defined?",
        a: "Based on the business context, the goals and what research points to as a priority. Scope is agreed in writing before starting and states what is executed, what is specified for your team and what is out of scope. In projects with advertising, consulting fees are independent from media spend.",
      },
      {
        q: "How is progress measured?",
        a: "With the sources available in your own environment: Search Console, analytics, ad platforms and your record of enquiries. Visibility, opportunities generated and contact quality are reviewed. No rankings, traffic volume or specific return is promised.",
      },
    ],
  },
} as const;

export const SceneFaq = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language);

  return (
    <section className="v3-section bg-white" aria-label={c.aria}>
      <div className="v3-shell grid gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <SceneHead index="08" label={c.label} title={c.title} />
          <Link to={routes.faq} className="v3-inline-link mt-7">
            {c.cta}
          </Link>
        </div>

        <div className="lg:col-span-8">
          {c.faqs.map((f, i) => (
            <details key={f.q} className="v3-details" open={i === 0}>
              <summary>
                <span>{f.q}</span>
              </summary>
              <div className="v3-details-body">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};
